import { useCallback, useEffect, useState } from 'react';
import { addDays, weekdayOf } from '../../domain/calendar.js';
import type { IsoDate, Weekday } from '../../domain/types.js';

export type View = 'plan' | 'lunches' | 'staples' | 'store' | 'cupboard' | 'list' | 'settings';

const BACK_FROM_MONDAY: Record<Weekday, number> = {
  mon: 0, tue: -1, wed: -2, thu: -3, fri: -4, sat: -5, sun: -6,
};

/**
 * The Monday of that date's week. Sunday belongs to the week that STARTED six
 * days earlier, not the one about to begin — which is how a British week reads
 * and what `weekDates()` expects, since it throws on anything but a Monday.
 */
export function mondayOf(date: IsoDate): IsoDate {
  return addDays(date, BACK_FROM_MONDAY[weekdayOf(date)]);
}

export function useHistoryNav<V extends string>(initial: V): {
  view: V;
  go: (v: V) => void;
  back: () => void;
} {
  const [view, setView] = useState<V>(initial);

  // Anchor the CURRENT history entry to the initial view on mount, so `back()`
  // after the first `go()` lands somewhere defined — regardless of whatever
  // state (or none) occupied this entry before the app took it over.
  useEffect(() => {
    window.history.replaceState({ view: initial }, '');
    // Deliberately mount-only: re-running on every `initial` change would
    // overwrite an entry the user has already navigated away from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onPop = (event: PopStateEvent): void => {
      const next = (event.state as { view?: V } | null)?.view;
      setView(next ?? initial);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [initial]);

  const go = useCallback((next: V) => {
    window.history.pushState({ view: next }, '');
    setView(next);
  }, []);

  const back = useCallback(() => {
    window.history.back();
  }, []);

  return { view, go, back };
}
